import { Component, input, output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ModalComponent } from '../../../shared/modal/modal.component';
import { AuthApiService } from '../../../core/services/auth-api.service';
import { AuthService } from '../../../core/services/auth.service';
import {
  FirebaseSsoService,
  firebaseAuthErrorMessage,
} from '../../../core/services/firebase-sso.service';
import type { AuthResponse } from '../../../core/types/auth.types';
import { FORM_FIELD_LIMITS } from '../../../core/constants/form-field-limits';
import { isValidEmailFormat } from '../../../core/utils/form-field-validation';

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './login-modal.component.html',
  styleUrl: './login-modal.component.scss',
})
export class LoginModalComponent {
  readonly limits = FORM_FIELD_LIMITS;

  isOpen = input.required<boolean>();

  closed = output<void>();
  loginSuccess = output<void>();
  loginError = output<string>();
  registerClick = output<void>();
  forgotPasswordClick = output<void>();

  private authApi = inject(AuthApiService);
  private authService = inject(AuthService);
  private firebaseSso = inject(FirebaseSsoService);

  email = signal('');
  password = signal('');
  showPassword = signal(false);
  loading = signal(false);
  errorMessage = signal('');

  onClose(): void {
    this.clearError();
    this.closed.emit();
  }

  clearError(): void {
    this.errorMessage.set('');
  }

  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  onSubmit(): void {
    const e = this.email().trim();
    const p = this.password();
    this.errorMessage.set('');
    if (!e) {
      this.errorMessage.set('Informe o e-mail.');
      return;
    }
    if (!isValidEmailFormat(e)) {
      this.errorMessage.set('Informe um e-mail válido.');
      return;
    }
    if (!p) {
      this.errorMessage.set('Informe a senha.');
      return;
    }
    if (p.length > this.limits.passwordMax) {
      this.errorMessage.set(`A senha deve ter no máximo ${this.limits.passwordMax} caracteres.`);
      return;
    }
    this.loading.set(true);
    this.authApi.login({ email: e, password: p }).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res?.data) {
          this.authService.setSession(res as AuthResponse);
        }
        this.loginSuccess.emit();
        this.onClose();
      },
      error: (err) => {
        this.loading.set(false);
        const message =
          err?.error?.message ??
          err?.error?.error ??
          (typeof err?.error === 'string' ? err.error : null) ??
          'E-mail ou senha inválidos. Tente novamente.';
        this.errorMessage.set(message);
        this.loginError.emit(message);
      },
    });
  }

  onForgotPassword(): void {
    this.forgotPasswordClick.emit();
  }

  onGoogleLogin(): void {
    this.startFirebaseSso(() => this.firebaseSso.signInWithGoogle());
  }

  onAppleLogin(): void {
    this.startFirebaseSso(() => this.firebaseSso.signInWithApple());
  }

  /**
   * Popup Firebase → ID token → `POST /api/Auth/firebase` → mesma sessão que login por e-mail.
   */
  private startFirebaseSso(getToken: () => Observable<string>): void {
    if (!this.firebaseSso.isConfigured()) {
      this.errorMessage.set(
        'Login social não está configurado. Peça ao administrador para definir `firebase` em `environment` e o endpoint da API (ver docs/firebase-sso-setup.md).',
      );
      return;
    }
    this.errorMessage.set('');
    this.loading.set(true);
    getToken()
      .pipe(switchMap((idToken) => this.authApi.loginWithFirebase({ idToken })))
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          if (res?.data) {
            this.authService.setSession(res as AuthResponse);
          }
          this.loginSuccess.emit();
          this.onClose();
        },
        error: (err) => {
          this.loading.set(false);
          const apiMsg =
            err?.error?.message ??
            err?.error?.error ??
            (typeof err?.error === 'string' ? err.error : null);
          const message =
            typeof apiMsg === 'string' && apiMsg.length > 0
              ? apiMsg
              : firebaseAuthErrorMessage(err);
          this.errorMessage.set(message);
          this.loginError.emit(message);
        },
      });
  }

  onRegister(): void {
    this.registerClick.emit();
  }
}
